/* Loka / Claude — Logo list (Loka) auto-scroll marquee — 2026-07-03
   Pulled from the "New Homepage Wireframe (with new sections)" draft theme
   (Shopify theme #152770019484). The live theme's original logo-list.js has
   no auto-scroll/marquee support at all — this is a newer version of the
   component, kept as a separate file (rather than replacing the shared
   logo-list.js) so the existing Logo list section on the Ritual campaign
   page, which uses the older version, is completely unaffected.

   Paired with sections/logo-list-loka.liquid.
*/
(() => {
  // Total loop duration (ms) for a full marquee cycle. Fixed values give
  // consistent perceived speed regardless of how many logos are shown.
  const MARQUEE_DURATION = { slow: 40000, medium: 22000, fast: 10000 };

  // ── Entrance animation (desktop only) ──────────────────────────
  const initAnimation = (section) => {
    const list = section.querySelector('.logo-list');
    if (!list || list.dataset.desktopAnimation !== 'true') return;

    const items = list.querySelectorAll('.logo-list__item');
    const repeat = list.dataset.repeatDesktopAnimation === 'true';
    let animated = false;

    const hide = () => items.forEach(el => {
      el.style.opacity = 0;
      el.style.transform = 'translate3d(0, 15px, 0)';
      el.style.transition = '';
    });

    const show = () => items.forEach(el => {
      el.style.opacity = 1;
      el.style.transform = '';
      el.style.transition = '';
    });

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!repeat && animated) { show(); return; }
        if (entry.isIntersecting) {
          items.forEach((el, i) => {
            el.style.transition = `all var(--duration-long) ease-in-out calc(150ms * ${i + 1})`;
            el.style.opacity = 1;
            el.style.transform = 'none';
          });
          animated = true;
        } else {
          hide();
        }
      });
    }, { threshold: 1 });

    const check = () => {
      if (document.body.clientWidth >= 576) {
        if (!repeat && animated) {} else hide();
        observer.observe(list);
      } else {
        show();
        observer.unobserve(list);
      }
    };

    check();
    window.addEventListener('resize', check);
  };

  // ── CSS marquee (no Swiper, no timing glitches) ────────────────
  const initMarquee = (section) => {
    const list    = section.querySelector('.logo-list');
    const wrapper = section.querySelector('.logo-list__wrapper');
    if (!list || !wrapper) return;

    // Run once per element. Without this, every re-entry into the viewport - which
    // includes a bfcache restore when you navigate back to the page - cloned the
    // slides again (9 -> 18 -> 36). The keyframe is a fixed translateX(0 -> -50%)
    // over a fixed duration, so each doubling of the track doubled the apparent
    // scroll speed. That was the "logos suddenly flying past" bug.
    if (list.dataset.marqueeInit === 'true') return;
    list.dataset.marqueeInit = 'true';

    const speedKey = list.dataset.autoScrollSpeed || 'medium';
    const duration = MARQUEE_DURATION[speedKey] ?? MARQUEE_DURATION.medium;

    // Duplicate slides so the loop is seamless: the animation runs
    // translateX(0 → -50%), which is exactly one full set of logos.
    const originals = [...wrapper.querySelectorAll('.swiper-slide')];
    originals.forEach(slide => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(clone);
    });

    wrapper.style.setProperty('--marquee-duration', `${duration}ms`);
    list.classList.add('logo-list--marquee');
  };

  // ── Swiper init (manual mode) ──────────────────────────────────
  const initSection = (section) => {
    if (!section?.classList.contains('section-logo-list')) return;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        // One-shot: this observer only exists to defer set-up until the section is
        // near the viewport. Leaving it connected re-ran set-up on every re-entry.
        observer.disconnect();

        const list       = section.querySelector('.logo-list');
        const autoScroll = list?.dataset.autoScroll === 'true';

        if (autoScroll) {
          initMarquee(section);
          return;
        }

        const slider = section.querySelector('.js-slider-logos');
        if (!slider) { initAnimation(section); return; }

        if (slider.classList.contains('swiper-initialized')) { initAnimation(section); return; }

        new Swiper(slider, {
          slidesPerView: 'auto',
          loop: false,
          speed: 800,
          navigation: {
            prevEl: slider.parentNode.querySelector('.swiper-button-prev'),
            nextEl: slider.parentNode.querySelector('.swiper-button-next'),
          },
          breakpoints: {
            1200: { mousewheel: true, grabCursor: true },
          },
        });

        initAnimation(section);
      });
    });
    observer.observe(section);
  };

  initSection(document.currentScript.parentElement);
  document.addEventListener('shopify:section:load', e => initSection(e.target));
})();
